/**
 * Loan Book Resolution Utility
 *
 * Converts a loan book selection (from UI dropdown/tabs) into the query
 * parameters that backend callables expect (templateId + optional entityCode).
 *
 * Used by every page with a loan book selector to bridge between the
 * loan book abstraction and the existing query function parameters.
 *
 * CRITICAL: entityCode filter goes in OUTER WHERE only, NEVER inside
 * canonical_office CTE — but that's enforced server-side, not here.
 */

import type { ResolvedLoanBook } from '@valutten/shared-schemas';

export interface LoanBookQueryParams {
  templateId?: string;
  entityCode?: string;
}

/**
 * Resolve a loan book selection to query parameters.
 *
 * Three input shapes:
 *   - 'all'                    → no filter
 *   - 'entity:<entityCode>'    → entity-grouped tab (multiple loan_books share an
 *                                 entityCode, e.g. VFA's Choice + LMG → 'LOAN_MARKET').
 *                                 Returns { entityCode } only — backend unions all
 *                                 underlying templates that match the entity.
 *   - '<loanBookId>'           → single loan_book (legacy + Timms one-per-entity case).
 *                                 Returns { templateId, entityCode } as before.
 *
 * @param selectedLoanBookId - 'all', 'entity:<code>', or a specific loan book ID
 * @param loanBooks - available loan books from useLoanBooks()
 * @returns { templateId?, entityCode? } for passing to query hooks
 */
export function resolveLoanBookSelection(
  selectedLoanBookId: string,
  loanBooks: ResolvedLoanBook[]
): LoanBookQueryParams {
  if (!selectedLoanBookId || selectedLoanBookId === 'all') {
    return {};
  }

  // Entity-grouped tab (one entityCode spans multiple loan_books).
  // Filter by entityCode only — DO NOT narrow by templateId because the entity
  // by definition spans more than one template (e.g. Choice + LMG).
  if (selectedLoanBookId.startsWith('entity:')) {
    const entityCode = selectedLoanBookId.slice('entity:'.length);
    if (entityCode) return { entityCode };
    return {};
  }

  const loanBook = loanBooks.find((lb) => lb.id === selectedLoanBookId);
  if (!loanBook) {
    return {};
  }

  if (loanBook.type === 'entity' && loanBook.entityCode) {
    return {
      templateId: loanBook.templateId,
      entityCode: loanBook.entityCode,
    };
  }

  return {
    templateId: loanBook.templateId,
  };
}

/**
 * Decide which option the Upload page's template/loan-book `<select>` should be
 * seeded to, so that the DISPLAYED option always equals the SUBMITTED value.
 *
 * The bug this closes (AMAG/Vision, 2026-06): the `<select>` renders options from
 * `loanBooks.length > 0 ? loanBooks : templates` (option value = item.id), but the
 * reset effect seeded `selectedTemplate` from `userVisibleTemplates[0].id` (a
 * *fileTemplate* id). For unified tenants the seeded template id matches NO loan-book
 * option, so the browser shows option[0] (e.g. "Vision (AMAG)") while state holds a
 * different template id (e.g. Choice) — "shows X, submits Y". A no-touch submit then
 * sent the wrong template → wrong parser → 0 records → silent failure.
 *
 * This helper seeds/validates against the SAME option source the `<select>` renders,
 * so the mismatch is unrepresentable.
 *
 * Preference order when the current selection is not a valid option:
 *   1. `templatesList[0].id` when it IS an option — legacy loan_books are synthesized
 *      with `id === templateId` (getLoanBooks.ts), so legacy tenants stay
 *      byte-identical with today's `setSelectedTemplate(templatesList[0].id)` default.
 *   2. First option whose underlying `templateId` is user-visible — `getLoanBooks` is
 *      company-wide (no per-user filter) while `userVisibleTemplates` IS RBAC-filtered,
 *      so a restricted office_admin's default never lands on a book outside their scope.
 *   3. `optionIds[0]` — fallback when the visible-template set is empty/unloaded.
 *
 * @returns the id to seed, or `null` when no change is needed (empty options, or the
 *          current selection is already a valid option — never clobber a valid pick).
 */
export function resolveUploadSeedSelection(
  loanBooks: ResolvedLoanBook[],
  templatesList: Array<{ id: string }>,
  current: string
): string | null {
  // SAME expression as the <select> option source in UploadPage.tsx.
  const optionSource: Array<{ id: string; templateId?: string }> =
    loanBooks.length > 0 ? loanBooks : templatesList;
  const optionIds = optionSource.map((item) => item.id);

  // Nothing to seed from, or the current selection is already a valid option → no-op.
  // The latter guarantees a manually-chosen loan book is never clobbered on re-render.
  if (optionIds.length === 0 || optionIds.includes(current)) {
    return null;
  }

  // Preference 1: legacy-preserving default (id === templateId for synthesized books).
  if (templatesList[0]?.id && optionIds.includes(templatesList[0].id)) {
    return templatesList[0].id;
  }

  // Preference 2: RBAC-aware — first option whose templateId is user-visible.
  const visibleIds = new Set(templatesList.map((t) => t.id));
  const rbacPreferred = optionSource.find(
    (b) => b.templateId !== undefined && visibleIds.has(b.templateId)
  );

  // Preference 3: fallback to the first rendered option.
  return rbacPreferred?.id ?? optionIds[0];
}

/**
 * Office-scope entity-type loan books for an office dashboard page (PR #1510).
 *
 * Entourage post-flip is the first MULTI-office unified tenant: without this,
 * every office page's loan-book tabs would show ALL entity books company-wide
 * (e.g. Geelong showing all 15). Entity books carry `officeId` (optional per
 * LoanBookSchema), so an office page keeps only its own books:
 *   - Geelong → its 3 books; Cremorne → its own; etc.
 *
 * Safety fallbacks (current behaviour preserved exactly):
 *   (a) No officeId given (route/offices still loading), OR no entity book
 *       carries a matching officeId (single-office tenants whose books lack
 *       officeId, or an office with zero books) → the UNFILTERED list.
 *   (b) A book MISSING officeId is never hidden (kept on every office page) —
 *       hiding it would make its data unreachable from office dashboards.
 *   (c) Non-entity (template-type) books pass through untouched — the caller's
 *       legacy branch owns their office filtering (templateIds-with-data).
 *
 * Single-office unified tenants (Timms/Decru/VFA) are byte-identical either
 * way: books without officeId hit fallback (a); books with officeId can only
 * reference the tenant's sole office, so the filter keeps everything.
 */
export function filterEntityBooksForOffice(
  loanBooks: ResolvedLoanBook[],
  officeId: string | undefined
): ResolvedLoanBook[] {
  if (!officeId || loanBooks.length === 0) return loanBooks;
  const anyMatch = loanBooks.some((lb) => lb.type === 'entity' && lb.officeId === officeId);
  if (!anyMatch) return loanBooks;
  return loanBooks.filter(
    (lb) => lb.type !== 'entity' || !lb.officeId || lb.officeId === officeId
  );
}

/**
 * Get display name for the "All" option based on company mode.
 */
export function getAllLoanBooksLabel(loanBooks: ResolvedLoanBook[]): string {
  if (loanBooks.length === 0) return 'All';
  const hasEntityType = loanBooks.some((lb) => lb.type === 'entity');
  return hasEntityType ? 'All Loan Books' : 'All Loan Books';
}

/**
 * Display option for a loan-book selector dropdown.
 *
 * For entity-typed loan_books that share an entityCode (e.g. VFA's Choice + LMG
 * both map to LOAN_MARKET), this collapses them into ONE option with
 * value='entity:<code>' that the resolver expands back to the full template set.
 *
 * For non-entity loan_books (Entourage legacy mode), each loan_book gets its own
 * entry as before.
 *
 * Use this helper for ALL dropdowns that previously did `loanBooks.map(...)`.
 */
export interface LoanBookDisplayOption {
  value: string;
  label: string;
}

export function getDisplayLoanBooks(loanBooks: ResolvedLoanBook[]): LoanBookDisplayOption[] {
  if (!loanBooks || loanBooks.length === 0) return [];

  const entityTypeBooks = loanBooks.filter(lb => lb.type === 'entity' && lb.entityCode);
  const nonEntityBooks = loanBooks.filter(lb => !(lb.type === 'entity' && lb.entityCode));

  // Collapse entity-typed loan_books by entityCode. First-seen-name wins so that
  // the dropdown is deterministic across renders (loanBooks order is stable).
  const byEntityCode = new Map<string, LoanBookDisplayOption>();
  for (const lb of entityTypeBooks) {
    if (!byEntityCode.has(lb.entityCode!)) {
      // Strip a trailing parenthetical from the display name so "Loan Market (LMG)"
      // becomes just "Loan Market" — the parenthetical was per-template metadata
      // that's no longer relevant once Choice + LMG are merged into one entity.
      const cleanName = (lb.name || lb.entityCode!).replace(/\s*\([^)]*\)\s*$/, '').trim();
      byEntityCode.set(lb.entityCode!, {
        value: `entity:${lb.entityCode}`,
        label: cleanName || lb.name || lb.entityCode!,
      });
    }
  }

  return [
    ...Array.from(byEntityCode.values()),
    ...nonEntityBooks.map(lb => ({ value: lb.id, label: lb.name || lb.id })),
  ];
}
